import { errorHelpers } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { schemas } from 'modules/data';
import { useCallback, useEffect, useMemo } from 'react';
import type { MatchingInfoNormalizer } from 'shared/components';
import { mapMatchingInfo } from 'shared/components';
import {
    LineItemNormalizer,
    qBooksMatchingPopupAccountLineItemNormalizerState,
} from 'shared/components/QBooksMatchingPopup';
import { useLazyGetAccountLineItemMatchingSuggestions } from 'shared/data/webApp/v2';
import { useCompanyBasedOnUrl, useCompanyBetaFeatures } from 'shared/hooks';

import { ExpandedQBooksAccountLineItem } from '../../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { useRequestExpanded } from '../../hooks';

const useConstructAccountLineItem = (
    request:
        | selectors.types.QBooksBillExpandedRequest
        | selectors.types.QBooksPoExpandedRequest
        | selectors.types.QBooksExpenseExpandedRequest,
    accountLineItems: ExpandedQBooksAccountLineItem[]
) => {
    // TODO: Try to get rid of useLazyGet later
    const [triggerGetAccountLineItemMatchingSuggestions] = useLazyGetAccountLineItemMatchingSuggestions();

    const constructAccountLineItem = useCallback(
        async (lineItemId: string) => {
            const accountLineItem = accountLineItems.find((lineItem) => lineItem.id === lineItemId);
            const data = await triggerGetAccountLineItemMatchingSuggestions({
                params: {
                    path: { companyId: request.companyId },
                    query: {
                        requestId: request.id,
                        vendorId: request.details.vendor?.id ?? '',
                        billDate: request.details.date,
                        accountItemId: accountLineItem?.account?.id ?? '',
                    },
                },
            });

            const purchaseOrder = data.purchaseOrders?.find((purchaseOrder) =>
                purchaseOrder.accountBasedLines?.some(
                    (accountBasedLine) => accountBasedLine.lineItemId === accountLineItem?.matchedLineItemId
                )
            );

            if (!purchaseOrder) {
                return;
            }

            const { accountBasedLines, itemBasedLines, ...rest } = purchaseOrder;

            return mapMatchingInfo({
                ...rest,
                // TODO: BE don't have time to support correct types between v2 and v1, and we don't have time
                //  to wait, so as a temporary solution we casting types
                //  @see https://approvalmax.atlassian.net/browse/AM-20926
                accountBasedLine: accountBasedLines?.find(
                    (accountBasedLine) => accountBasedLine.lineItemId === accountLineItem?.matchedLineItemId
                ) as Parameters<typeof mapMatchingInfo>[0]['accountBasedLine'],
            });
        },
        [
            accountLineItems,
            triggerGetAccountLineItemMatchingSuggestions,
            request.companyId,
            request.details.date,
            request.details.vendor?.id,
            request.id,
        ]
    );

    return { constructAccountLineItem };
};

export const useMatchingInfoNormalizer = (accountLineItems: ExpandedQBooksAccountLineItem[]) => {
    const company = useCompanyBasedOnUrl();
    const { isQBOMatchingOnEditing } = useCompanyBetaFeatures(company.id);

    const request = useRequestExpanded();

    const { constructAccountLineItem } = useConstructAccountLineItem(request, accountLineItems);

    const setAccountLineItemNormalizer = useSetAtom(qBooksMatchingPopupAccountLineItemNormalizerState);

    const amountSumPerAccount = useMemo(
        () =>
            accountLineItems.reduce<Record<string, number>>((acc, cur) => {
                if (cur.matchedLineItemId) {
                    acc[cur.matchedLineItemId] = (acc[cur.matchedLineItemId] || 0) + (cur.amount ?? 0);
                }

                return acc;
            }, {}),
        [accountLineItems]
    );

    const accountLineItemNormalizer = useCallback<LineItemNormalizer<ReturnType<typeof schemas.mapAccountLineItem>>>(
        (value) => {
            return {
                ...value,
                remainingBalanceOrig: value.remainingBalance,
                remainingBalance: (value.remainingBalanceForRequest || 0) - (amountSumPerAccount?.[value.id] || 0),
            };
        },
        [amountSumPerAccount]
    );

    const matchingInfoNormalizer = useCallback<MatchingInfoNormalizer>(
        async (value, lineItemId) => {
            if (!isQBOMatchingOnEditing) {
                return value;
            }

            /*
             * The case:
             * - initially we have an unmatched line.
             * - we match it with some PO
             * - we see that the icon has changed to "matched", which can be used to display a popup with matching info
             * - when the popup window opens we are not aware that the line was matched locally
             * Thus our goal is to fill the matched line with the data we've gathered
             */
            const gatheredValue = value?.id ? value : await constructAccountLineItem(lineItemId);

            if (!gatheredValue || !gatheredValue.accountLineItem) {
                throw errorHelpers.notFoundError();
            }

            return {
                ...gatheredValue,
                accountLineItem: accountLineItemNormalizer(gatheredValue.accountLineItem),
            };
        },
        [accountLineItemNormalizer, constructAccountLineItem, isQBOMatchingOnEditing]
    );

    useEffect(() => {
        setAccountLineItemNormalizer(accountLineItemNormalizer);
    }, [accountLineItemNormalizer, setAccountLineItemNormalizer]);

    return matchingInfoNormalizer;
};
